// GET /admin/dashboard, and what needs an operator's attention in it.
// Shared by the dashboard page and the navbar's notifications, so both
// list the same things in the same order.

import { ref } from 'vue'
import { api } from '@/api/client'
import { useAuthStore } from '@/store/auth'

export interface DashSummary {
  sites: { total: number; active: number; draft: number; disabled: number; suspended: number }
  /** serving_alerts: nodes with an open 配置生效 alarm. */
  nodes: { total: number; online: number; lost: number; pending: number; stopped: number; serving_alerts?: number }
  users: { total: number; active: number }
  subscriptions: { active: number; expiring: number; expired: number; cancelled: number }
  certs: { total: number; expiring: number; expired: number }
  /** last_failed_at: when the newest of the day's failures failed. */
  tasks: { pending: number; failed: number; last_failed_at?: string }
  days: Array<{ date: string; sites: number; users: number; subscriptions: number }>
  /** 累计充值: paid top-ups in all and today, in 分. */
  recharge?: { total_cents: number; today_cents: number }
  /** 数据统计: totals the rest of the dashboard does not show. */
  counts: {
    /** Every name the sites answer on: primaries and aliases. */
    hostnames: number
    /** Layer-4 forwarding rules — 0 until the platform has L4 forwarding. */
    l4_forwards: number
    line_groups: number
    node_ips: number
    /** The platform's own lists, not customers'. */
    deny_ips: number
    allow_ips: number
    active_bans: number
  }
  /** 工单 waiting on support: new or answered by the customer, and nobody's. */
  tickets?: { open: number; unassigned: number }
  /**
   * The licence's standing: mode as GET /license says it (none, active,
   * expired, refused, dev); renew_overdue, half the lease gone without a
   * renewal.
   */
  license?: { mode: string; renew_overdue: boolean }
}

export interface Attention {
  /** How many: the message's {n}. */
  n: number
  /** dashboard.tag_<tag>: what kind of thing it is about. */
  tag: 'node' | 'cert' | 'site' | 'sub' | 'task' | 'ticket' | 'license'
  color: string
  /** dashboard.<key>: the message. */
  key: string
  /** The page that lists them. */
  route: string
}

// The newest summary any page loaded: 任务队列 marks failures seen from it
// without loading one of its own.
export const latestSummary = ref<DashSummary | null>(null)

export async function loadDashSummary(): Promise<DashSummary> {
  // Minutes east of UTC, so "today" is the operator's today.
  const s: DashSummary = await api.adminDashboard({ tz_offset: -new Date().getTimezoneOffset() })
  latestSummary.value = s
  return s
}

// 失败任务 is news, not a state like a lost node: once an operator has
// looked — clicked it, or opened 任务队列 — it is left out until something
// fails after that. Per operator, in this browser: {user id: failed at}.
const SEEN_KEY = 'cdnx_failures_seen'
const seen = ref<Record<string, string>>(readSeen())
// Seen in another tab is seen here too.
window.addEventListener('storage', (e) => {
  if (e.key === SEEN_KEY) seen.value = readSeen()
})

function readSeen(): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}')
    return v && typeof v === 'object' ? v : {}
  } catch {
    return {}
  }
}

function uid(): string {
  return String(useAuthStore().user?.id ?? 0)
}

function failuresSeen(s: DashSummary): boolean {
  const last = s.tasks.last_failed_at
  const at = seen.value[uid()]
  return !!last && !!at && Date.parse(last) <= Date.parse(at)
}

// Marks the newest failure in the latest summary seen.
export function markFailuresSeen() {
  const last = latestSummary.value?.tasks.last_failed_at
  if (!last || failuresSeen(latestSummary.value!)) return
  seen.value = { ...seen.value, [uid()]: last }
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen.value))
  } catch {
    // Private mode: seen until the page is reloaded.
  }
}

// Worst first. Only the ones that are not zero are listed; an empty list
// says all is well.
export function attentionItems(s: DashSummary | null | undefined): Attention[] {
  if (!s) return []
  // The licence first: without one the customer console is read-only.
  const lic = s.license?.mode
  const rows: Attention[] = [
    { n: lic === 'none' ? 1 : 0, tag: 'license', color: 'red', key: 'licenseNone', route: 'license' },
    { n: lic === 'expired' ? 1 : 0, tag: 'license', color: 'red', key: 'licenseExpired', route: 'license' },
    { n: lic === 'refused' ? 1 : 0, tag: 'license', color: 'red', key: 'licenseRefused', route: 'license' },
    { n: lic === 'active' && s.license?.renew_overdue ? 1 : 0, tag: 'license', color: 'orangered', key: 'licenseOverdue', route: 'license' },
    { n: s.nodes.lost, tag: 'node', color: 'red', key: 'lostNodes', route: 'nodes' },
    { n: s.nodes.serving_alerts ?? 0, tag: 'node', color: 'red', key: 'servingAlerts', route: 'nodes' },
    { n: s.certs.expired, tag: 'cert', color: 'red', key: 'certsExpired', route: 'certificates' },
    { n: s.certs.expiring, tag: 'cert', color: 'orangered', key: 'certsExpiring', route: 'certificates' },
    { n: s.tickets?.open ?? 0, tag: 'ticket', color: 'orange', key: 'ticketsOpen', route: 'tickets' },
    { n: s.sites.suspended, tag: 'site', color: 'orangered', key: 'sitesSuspended', route: 'domains' },
    { n: s.subscriptions.expiring, tag: 'sub', color: 'orange', key: 'subsExpiring', route: 'subscriptions' },
    { n: failuresSeen(s) ? 0 : s.tasks.failed, tag: 'task', color: 'orange', key: 'tasksFailed', route: 'tasks' },
    { n: s.nodes.pending, tag: 'node', color: 'blue', key: 'pendingNodes', route: 'nodes' },
    { n: s.sites.draft, tag: 'site', color: 'cyan', key: 'sitesDraft', route: 'domains' },
  ]
  return rows.filter((r) => r.n > 0)
}
